import { BACKEND_URL } from '..';
import { CmsClient } from '../../cms/sanity';
import { APIEndpoint, RequestInput } from './types';

export async function sendRequest<T>(
  endpoint: APIEndpoint,
  input?: RequestInput
): Promise<T> {
  const req = makeRequest(endpoint, input || {});
  try {
    const res = await fetch(req, {
      credentials: 'include',
    });
    if (!res.ok) {
      if (res.status === 401 && endpoint.path.endsWith('/me')) {
        localStorage.clear();
      }
      const errorResponse = await res.json();
      const errorMessage = errorResponse.message || 'Request failed';
      throw new Error(`HTTP Error ${res.status}: ${errorMessage}`);
    }
    const jsonResponse = await res.json();
    return jsonResponse;
  } catch (error) {
    console.error('API Error:', error);
    throw error;
  }
}

export const makeRequest = (
  endpoint: APIEndpoint,
  input: RequestInput
): RequestInfo => {
  const { method, path, pathParams, queryParams } = endpoint;

  const fixedPath =
    BACKEND_URL + constructPathWithParams(path, pathParams, input.pathParams);
  const queryString = constructQueryString(queryParams, input.queryParams);

  let jsonBody = '';
  if (input.body) {
    jsonBody = JSON.stringify(input.body);
  }

  const ctrl = new AbortController();
  const sig = ctrl.signal;

  setTimeout(() => ctrl.abort(), 20000);

  return new Request(fixedPath + queryString, {
    body: jsonBody || null,
    method: method,
    signal: sig,
    headers: { 'Content-Type': 'application/json' },
  });
};

// handles path parameter replacement
const constructPathWithParams = (
  path: string,
  pathParams: string[] | undefined,
  inputPathParams: Record<string, string> | undefined
): string => {
  if (pathParams?.length && inputPathParams) {
    let constructedPath = path;
    for (const param of pathParams) {
      const paramValue = inputPathParams[param] || '';
      constructedPath = constructedPath.replace(`/:${param}`, `/${paramValue}`);
    }
    return constructedPath;
  }
  return path;
};

//  filters and maps the query parameters
const constructQueryString = (
  queryParams: string[] | undefined,
  inputQueryParams: Record<string, string> | undefined
): string => {
  if (queryParams?.length && inputQueryParams) {
    const params = Object.entries(inputQueryParams)
      .filter(([key]) => queryParams.includes(key))
      .map(([key, value]) => `${key}=${value}`);
    if (params.length) {
      return `?${params.join('&')}`;
    }
  }
  return '';
};

export async function fetchSanitySettings() {
  return await CmsClient.fetchSettings();
}
